<template>
  <el-dialog
    title="BULK CREATE PRODUCT TYPES"
    :visible.sync="dialogIsOpen"
    :fullscreen="true"
    :close-on-click-modal="false"
    append-to-body
    class="product-type-bulk-dialog"
    @opened="handleOpened"
    @closed="closeDialog"
  >
    <div class="product-type-bulk-form">
      <div ref="spreadsheet" class="product-type-bulk-spreadsheet"></div>
      <div class="d-flex justify-content-start mt-3">
        <mdb-btn v-if="dependenciesLoading" color="elegant" size="sm" disabled>
          <span class="spinner-border spinner-border-sm mr-1"></span>
          Loading...
        </mdb-btn>
        <mdb-btn v-else color="elegant" size="sm" :disabled="loading" @click="submit">
          <span v-if="loading" class="spinner-border spinner-border-sm mr-1"></span>
          SAVE
        </mdb-btn>
        <mdb-btn color="grey" size="sm" :disabled="loading" @click="dialogIsOpen = false">CLOSE</mdb-btn>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { mdbBtn } from 'mdbvue'
import { Message, MessageBox } from 'element-ui'
import { mapActions } from 'vuex'
import jspreadsheet from '../../jsexcel/jexcel'
import { ApiHttpService } from '../../services'

export default {
  components: { mdbBtn },
  data() {
    return {
      dialogIsOpen: false,
      loading: false,
      dependenciesLoading: false,
      spreadsheet: null,
      departments: [],
      categories: [],
      optionsReady: false,
    }
  },
  methods: {
    ...mapActions('ProductTypeModule', ['bulkStore']),
    openDialog() {
      this.dialogIsOpen = true
      this.optionsReady = false
      this.dependenciesLoading = true
      this.$nextTick(() => {
        if (!this.spreadsheet) this.renderTable()
      })
      this.loadOptions()
    },
    handleOpened() {
      if (!this.spreadsheet) this.renderTable()
    },
    closeDialog() {
      this.dialogIsOpen = false
      if (this.spreadsheet) {
        this.spreadsheet.destroy()
        this.spreadsheet = null
      }
    },
    async loadOptions() {
      try {
        const [departmentResponse, categoryResponse] = await Promise.all([
          ApiHttpService.getRequest('product-departments'),
          ApiHttpService.getRequest('category-master/all'),
        ])
        this.departments = departmentResponse.data.data || departmentResponse.data || []
        this.categories = categoryResponse.data.data || categoryResponse.data || []
        this.optionsReady = true
        if (this.dialogIsOpen) this.$nextTick(() => this.renderTable())
      } finally {
        this.dependenciesLoading = false
      }
    },
    renderTable() {
      if (!this.$refs.spreadsheet) return
      if (this.spreadsheet) this.spreadsheet.destroy()
      this.spreadsheet = jspreadsheet(this.$refs.spreadsheet, {
        /* Positional, one slot per column in the order declared below:
           department, category, name, arabic name, description, status,
           sort order, response. Status defaults to Active. */
        data: Array.from({ length: 50 }, () => ['', '', '', '', '', '1', 0, '']),
        minDimensions: [8, 50],
        allowInsertRow: true,
        allowDeleteRow: true,
        allowInsertColumn: false,
        allowDeleteColumn: false,
        allowRenameColumn: false,
        tableWidth: '100%',
        tableOverflow: true,
        tableHeight: 'calc(100vh - 105px)',
        columns: [
          { type: 'dropdown', title: 'DEPARTMENT', name: 'department_id', width: 220, autocomplete: true, source: this.departments },
          { type: 'dropdown', title: 'CATEGORY', name: 'category_id', width: 260, autocomplete: true, source: this.categories },
          { type: 'text', title: 'NAME', name: 'name', width: 280 },
          { type: 'text', title: 'ARABIC NAME', name: 'arabic_name', width: 280, align: 'right' },
          { type: 'text', title: 'DESCRIPTION', name: 'description', width: 360 },
          { type: 'dropdown', title: 'STATUS', name: 'active', width: 140, source: [{ id: '0', name: 'Inactive' }, { id: '1', name: 'Active' }] },
          { type: 'numeric', title: 'SORT ORDER', name: 'sort_order', width: 120 },
          { type: 'text', title: 'RESPONSE', name: 'response', width: 360, readOnly: true },
        ],
        onbeforechange: (instance, cell, column, row, value) => {
          const grid = instance.jexcel || instance
          if (Number(column) === 1) {
            const departmentId = grid.getValueFromCoords(0, row)
            grid.options.columns[1].source = this.categories.filter(category => (
              departmentId && String(category.department_id) === String(departmentId)
            ))
          }
          return value
        },
        onchange: (instance, cell, column, row, value) => {
          const grid = instance.jexcel || instance
          if (Number(column) === 0) {
            grid.setValueFromCoords(1, row, '', true)
            grid.options.columns[1].source = this.categories.filter(category => (
              value && String(category.department_id) === String(value)
            ))
          }
        },
      })
      if (this.spreadsheet.setWidth) this.spreadsheet.setWidth(window.innerWidth)
    },
    getRows() {
      return this.spreadsheet.getData().map((row, index) => ({
        department_id: row[0],
        category_id: row[1],
        name: row[2] ? String(row[2]).trim() : '',
        arabic_name: row[3] ? String(row[3]).trim() : null,
        description: row[4] || null,
        active: row[5] === '' ? true : ['1', 1, true, 'true'].includes(row[5]),
        sort_order: row[6] === '' ? 0 : Number(row[6]),
        response: row[7] || '',
        row: index + 1,
      })).filter(row => row.department_id || row.category_id || row.name || row.description)
    },
    setResponse(message, rowNumber) {
      if (this.spreadsheet && rowNumber) {
        this.spreadsheet.setValueFromCoords(7, rowNumber - 1, message, true)
      }
    },
    setRowStatus(rowNumber, status) {
      if (!this.spreadsheet || !rowNumber) return
      const backgroundColor = status === 'success' ? '#04b65d' : 'pink'
      const columns = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
      columns.forEach(column => {
        this.spreadsheet.setStyle(column + rowNumber, 'background-color', backgroundColor, true)
      })
    },
    async submit() {
      const rows = this.getRows()
      if (!rows.length) {
        Message.warning('Add at least one Product Type row.')
        return
      }
      const errors = []
      const names = {}
      rows.forEach(row => {
        const category = this.categories.find(item => String(item.id) === String(row.category_id))
        if (!row.department_id) errors.push('Row ' + row.row + ': Department is required.')
        if (!row.category_id) errors.push('Row ' + row.row + ': Category is required.')
        if (!row.name) errors.push('Row ' + row.row + ': Name is required.')
        if (category && String(category.department_id) !== String(row.department_id)) {
          errors.push('Row ' + row.row + ': Category does not belong to the selected Department.')
        }
        const nameKey = row.category_id + ':' + row.name.toLowerCase()
        if (names[nameKey]) errors.push('Rows ' + names[nameKey] + ' and ' + row.row + ': duplicate Product Type name in the same Category.')
        names[nameKey] = row.row
      })
      if (errors.length) {
        errors.forEach(message => {
          const rowMatch = message.match(/Row(?:s)?\s+(\d+)/i)
          const rowNumber = rowMatch ? Number(rowMatch[1]) : 1
          this.setResponse(message, rowNumber)
          this.setRowStatus(rowNumber, 'error')
        })
        MessageBox.alert(errors.slice(0, 8).join('<br>'), 'Please check the spreadsheet', { dangerouslyUseHTMLString: true, type: 'error' })
        return
      }
      await MessageBox.confirm('Create ' + rows.length + ' Product Type' + (rows.length === 1 ? '' : 's') + '?', 'Confirm bulk create', { type: 'warning' })
      this.loading = true
      try {
        const payload = rows.map(({ row, response, ...item }) => item)
        const response = await this.bulkStore(payload)
        Message.success(response.message || 'Product Types created successfully.')
        rows.forEach(row => {
          this.setResponse(response.message || 'Created successfully.', row.row)
          this.setRowStatus(row.row, 'success')
        })
        this.$emit('success')
      } catch (error) {
        const responseData = error.response && error.response.data ? error.response.data : {}
        const message = responseData.message || 'Product Type bulk create failed.'
        const rowMatch = message.match(/row\s+(\d+)/i)
        const rowNumber = rowMatch ? Number(rowMatch[1]) : rows[0].row
        this.setResponse(message, rowNumber)
        this.setRowStatus(rowNumber, 'error')
        Message.error(message)
      } finally {
        this.loading = false
      }
    },
  },
  beforeDestroy() {
    if (this.spreadsheet) this.spreadsheet.destroy()
  },
}
</script>

<style scoped>
.product-type-bulk-form {
  min-height: calc(100vh - 80px);
}
.product-type-bulk-spreadsheet {
  width: 100%;
}
.product-type-bulk-dialog /deep/ .el-dialog__body {
  padding: 0;
}
</style>
